/** Pure rules for the Family Plan section of Configure Sales Panel (CWS-1582). */
import type {
  FamilyDiscountType,
  FamilyPlanSettings,
  FamilyPlanSettingsLimits,
  FamilyPlanSettingsPayload,
} from '../types/family-plan-settings.types';

export const FAMILY_SUGGESTED_VALUES: Record<FamilyDiscountType, readonly number[]> = {
  percentage: [5, 10, 15, 20, 30],
  flat: [2, 5, 10, 20, 30],
};

export const DEFAULT_FAMILY_LIMITS: FamilyPlanSettingsLimits = {
  max_vehicles_min: 2,
  max_vehicles_max: 20,
  max_presets: 10,
  percentage_max: 100,
  flat_max: 9999.99,
};

export interface FamilyPlanDraft {
  maxVehicles: number | null;
  discountType: FamilyDiscountType | null;
  /** Selected preset values for the chosen type, ascending. */
  values: number[];
  defaultValue: number | null;
  customEnabled: boolean;
}

export interface FamilyChip {
  value: number;
  selected: boolean;
  /** Custom values (not one of the suggestions) can be removed with the X. */
  removable: boolean;
}

export function emptyFamilyDraft(): FamilyPlanDraft {
  return {
    maxVehicles: null,
    discountType: null,
    values: [],
    defaultValue: null,
    customEnabled: false,
  };
}

function sortedUnique(values: number[]): number[] {
  return [...new Set(values)].sort((a, b) => a - b);
}

export function toFamilyDraft(settings: FamilyPlanSettings | null): FamilyPlanDraft {
  if (!settings) {
    return emptyFamilyDraft();
  }
  return {
    maxVehicles: settings.max_vehicles,
    discountType: settings.discount_type,
    values: sortedUnique(settings.presets),
    defaultValue: settings.default_discount_value,
    customEnabled: settings.custom_enabled,
  };
}

/** Suggested chips plus any saved or typed custom values, ascending. */
export function familyChips(draft: FamilyPlanDraft): FamilyChip[] {
  if (!draft.discountType) {
    return [];
  }
  const suggested = FAMILY_SUGGESTED_VALUES[draft.discountType];
  return sortedUnique([...suggested, ...draft.values]).map(value => ({
    value,
    selected: draft.values.includes(value),
    removable: !suggested.includes(value),
  }));
}

function withValues(draft: FamilyPlanDraft, values: number[]): FamilyPlanDraft {
  const next = sortedUnique(values);
  return {
    ...draft,
    values: next,
    defaultValue:
      draft.defaultValue !== null && next.includes(draft.defaultValue) ? draft.defaultValue : null,
  };
}

/** Replaces the selected values (from the chip listbox); the default is cleared if it was deselected. */
export function setFamilyValues(draft: FamilyPlanDraft, values: number[]): FamilyPlanDraft {
  return withValues(draft, values);
}

export function removeFamilyValue(draft: FamilyPlanDraft, value: number): FamilyPlanDraft {
  return withValues(
    draft,
    draft.values.filter(v => v !== value)
  );
}

/** Switching type drops the other type's values and the default (the lists are per type). */
export function setFamilyDiscountType(
  draft: FamilyPlanDraft,
  discountType: FamilyDiscountType
): FamilyPlanDraft {
  if (draft.discountType === discountType) {
    return draft;
  }
  return { ...draft, discountType, values: [], defaultValue: null };
}

function maxFor(type: FamilyDiscountType, limits: FamilyPlanSettingsLimits): number {
  return type === 'percentage' ? limits.percentage_max : limits.flat_max;
}

export interface AddFamilyValueResult {
  draft: FamilyPlanDraft;
  error: string | null;
}

/** Parses the typed value (max 2 decimals) and adds it as a selected chip. */
export function addFamilyCustomValue(
  draft: FamilyPlanDraft,
  raw: string,
  limits: FamilyPlanSettingsLimits
): AddFamilyValueResult {
  const text = (raw ?? '').trim();
  const type = draft.discountType;
  if (!type) {
    return { draft, error: 'Choose a discount type first.' };
  }
  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    return { draft, error: 'Enter a number with up to 2 decimals.' };
  }
  const value = Number(text);
  const max = maxFor(type, limits);
  if (value <= 0 || value > max) {
    return { draft, error: `Enter a value above 0 and up to ${max}.` };
  }
  if (draft.values.includes(value)) {
    return { draft, error: 'That value is already added.' };
  }
  if (draft.values.length >= limits.max_presets) {
    return { draft, error: `You can add up to ${limits.max_presets} values.` };
  }
  return { draft: withValues(draft, [...draft.values, value]), error: null };
}

export function validateFamilyDraft(
  draft: FamilyPlanDraft,
  limits: FamilyPlanSettingsLimits
): string | null {
  if (draft.maxVehicles === null) {
    return 'Select the number of vehicles allowed.';
  }
  if (draft.maxVehicles < limits.max_vehicles_min || draft.maxVehicles > limits.max_vehicles_max) {
    return `Vehicles allowed must be between ${limits.max_vehicles_min} and ${limits.max_vehicles_max}.`;
  }
  if (!draft.discountType) {
    return 'Select an allowed discount type.';
  }
  if (draft.values.length === 0) {
    return 'Select at least one discount value.';
  }
  if (draft.values.length > limits.max_presets) {
    return `You can select up to ${limits.max_presets} values.`;
  }
  if (draft.defaultValue === null || !draft.values.includes(draft.defaultValue)) {
    return 'Select a default discount.';
  }
  return null;
}

export function isFamilyDraftDirty(draft: FamilyPlanDraft, saved: FamilyPlanDraft): boolean {
  return JSON.stringify(draft) !== JSON.stringify(saved);
}

/** Call only after `validateFamilyDraft` returned null. */
export function buildFamilyPayload(
  locationId: string,
  draft: FamilyPlanDraft
): FamilyPlanSettingsPayload {
  return {
    location_id: locationId,
    max_vehicles: draft.maxVehicles as number,
    discount_type: draft.discountType as FamilyDiscountType,
    presets: draft.values,
    default_discount_value: draft.defaultValue as number,
    custom_enabled: draft.customEnabled,
  };
}

export function familyVehicleOptions(limits: FamilyPlanSettingsLimits): number[] {
  const count = Math.max(0, limits.max_vehicles_max - limits.max_vehicles_min + 1);
  return Array.from({ length: count }, (_, i) => limits.max_vehicles_min + i);
}

/** "5%" / "$5" label for a chip or the default dropdown. */
export function formatFamilyValue(type: FamilyDiscountType | null, value: number): string {
  return type === 'flat' ? `$${value}` : `${value}%`;
}
